import { useEffect, type FC, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "~/shared/ui/button";
import { IconClose } from "~/shared/ui/icon/icons";
import { cn } from "~/shared/lib/utils";

interface ConfirmationDialogProps {
    isOpen: boolean;
    onClose?: () => void;
    onConfirm?: () => Promise<void>;
    title: ReactNode;
    description: ReactNode;
    confirmText?: ReactNode;
    cancelText?: ReactNode;
    waitConfirm?: boolean;
    contentClassName?: string;
}

const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    confirmText,
    cancelText,
    waitConfirm,
    contentClassName,
}) => {
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose?.();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscapeKey);
        }

        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className={cn(
                    "w-full max-w-[338px] rounded-[10px] bg-secondary p-5 !pt-[19px]",
                    contentClassName,
                )}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-row items-center justify-between gap-3">
                    <div className="text-base font-bold">{title}</div>
                    <div
                        className="mr-[1px] mb-1 flex h-4 w-4 cursor-pointer items-center justify-center"
                        onClick={onClose}
                    >
                        <IconClose />
                    </div>
                </div>

                <p className="mt-[13px] text-base leading-[1.375] font-semibold text-tab-text">
                    {description}
                </p>

                <div className="mt-5 mr-[1px] flex gap-2.5">
                    {cancelText && (
                        <Button
                            onClick={onClose}
                            disabled={waitConfirm}
                            className="h-auto flex-1 bg-[#323235] py-2 whitespace-normal text-tab-text hover:brightness-120"
                        >
                            {cancelText}
                        </Button>
                    )}
                    {confirmText && (
                        <Button
                            onClick={onConfirm}
                            disabled={waitConfirm}
                            className={`h-auto flex-1 bg-[#CC1919] py-2 whitespace-normal text-white hover:brightness-120`}
                        >
                            {waitConfirm ? <Loader2 className="animate-spin" /> : confirmText}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfirmationDialog;
